import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { informesAPI, usuariosAPI, dashboardFichasAPI } from '../services/api';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../Dashboard.css';

const CLAVE_LAYOUT = 'dashboard_layout';
const GRID_COLS = 10;
const GRID_ROWS = 6;
const GRID_SIZE = GRID_COLS * GRID_ROWS;
const MIN_W = 1;
const MIN_H = 1;

const fmt = (n) => `$${parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

const renderFichaTortaAbonos = (data, area) => {
  const chartData = (data?.chartData || []).filter((d) => parseFloat(d.value) > 0);
  const cargados = parseFloat(data?.cargados_total) || 0;
  const cobrados = parseFloat(data?.cobrados_total) || 0;
  const pct = data?.porcentaje_cobrado ?? data?.valor ?? 0;
  const compact = area < 4;

  if (cargados <= 0) {
    return (
      <p className="dashboard-ficha-torta-empty">Sin abonos cargados en el período</p>
    );
  }

  const tooltipFmt = (value, name) => {
    const pctSlice = cargados > 0 ? ((value / cargados) * 100).toFixed(1) : '0';
    return [`${fmt(value)} (${pctSlice}%)`, name];
  };

  return (
    <div className={`dashboard-ficha-torta${compact ? ' dashboard-ficha-torta--compact' : ''}`}>
      <ResponsiveContainer width="100%" height={compact ? 120 : 160}>
        <PieChart>
          <Pie
            data={chartData.length ? chartData : [{ name: 'Sin datos', value: 1, color: '#bdc3c7' }]}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={compact ? 28 : 40}
            outerRadius={compact ? 48 : 64}
            paddingAngle={2}
          >
            {(chartData.length ? chartData : [{ color: '#bdc3c7' }]).map((entry, index) => (
              <Cell key={`slice-${index}`} fill={entry.color || '#3498db'} />
            ))}
          </Pie>
          <Tooltip formatter={tooltipFmt} />
          {!compact && <Legend />}
        </PieChart>
      </ResponsiveContainer>
      <p className="dashboard-ficha-torta-resumen">
        <span className="dashboard-ficha-torta-pct">{pct}%</span> cobrado
        <span className="dashboard-ficha-torta-montos">
          {fmt(cobrados)} / {fmt(cargados)}
        </span>
      </p>
    </div>
  );
};

// Convierte array antiguo gridLayout (id por celda) a items [{ id, x, y, w, h }]
function gridLayoutToItems(gridLayout, cols) {
  const items = [];
  const rows = Math.ceil((gridLayout.length || 0) / cols);
  for (let i = 0; i < (gridLayout.length || 0); i++) {
    const id = gridLayout[i];
    if (!id) continue;
    const x = i % cols;
    const y = Math.floor(i / cols);
    items.push({ id, x, y, w: 1, h: 1 });
  }
  return items;
}

// Orden por defecto: una fila de celdas (x,y) para cada item 1x1
function defaultItems(order, cols) {
  return order.map((id, i) => ({
    id,
    x: i % cols,
    y: Math.floor(i / cols),
    w: 1,
    h: 1,
  }));
}

// Set de celdas ocupadas: "x,y"
function occupiedSet(items) {
  const set = new Set();
  (items || []).forEach((it) => {
    for (let dy = 0; dy < it.h; dy++) {
      for (let dx = 0; dx < it.w; dx++) {
        set.add(`${it.x + dx},${it.y + dy}`);
      }
    }
  });
  return set;
}

// Item que contiene la celda (cx, cy)
function getItemAt(items, cx, cy) {
  return (items || []).find(
    (it) => cx >= it.x && cx < it.x + it.w && cy >= it.y && cy < it.y + it.h
  ) || null;
}

// Encontrar la siguiente celda libre en el grid (fila por fila)
function nextFreePosition(occupied, cols, rows) {
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (!occupied.has(`${x},${y}`)) return { x, y };
    }
  }
  return null;
}

const Dashboard = () => {
  const [customFichas, setCustomFichas] = useState([]);
  const [fichaValues, setFichaValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [layoutItems, setLayoutItems] = useState([]);
  const [loadingPref, setLoadingPref] = useState(true);
  const [dragId, setDragId] = useState(null);
  const gridRef = useRef(null);
  const layoutItemsRef = useRef(layoutItems);
  const resizeRef = useRef({ active: false, itemId: null, startW: 0, startH: 0, startX: 0, startY: 0 });
  layoutItemsRef.current = layoutItems;

  const loadCustomFichas = useCallback(async () => {
    try {
      const res = await dashboardFichasAPI.listar({ mostrar_en_dashboard: true, activo: true });
      if (res.success && Array.isArray(res.data)) setCustomFichas(res.data);
    } catch (err) {
      console.error('Error al cargar fichas dashboard:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCustomFichas().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [loadCustomFichas]);

  const customFichaIds = useMemo(
    () => (customFichas || []).map((f) => `ficha-${f.id}`),
    [customFichas]
  );
  const defaultOrder = useMemo(() => [...customFichaIds], [customFichaIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (loading) return;
      setLoadingPref(true);
      try {
        const res = await usuariosAPI.getPreferencias(CLAVE_LAYOUT);
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data[0] : res.data;
        const data = raw?.valor ?? null;
        let items = [];
        if (data && Array.isArray(data.items) && data.items.length > 0) {
          const validSet = new Set(defaultOrder);
          items = data.items.filter((it) => validSet.has(it.id));
          const hadStale = items.length !== data.items.length;
          if (hadStale) {
            usuariosAPI.savePreferencias(CLAVE_LAYOUT, { items }).catch(() => {});
          }
          const existingIds = new Set(items.map((it) => it.id));
          const missingFichaIds = defaultOrder.filter(
            (id) => String(id).startsWith('ficha-') && !existingIds.has(id)
          );
          if (missingFichaIds.length > 0) {
            let occupied = occupiedSet(items);
            const newItems = [];
            for (const id of missingFichaIds) {
              const pos = nextFreePosition(occupied, GRID_COLS, GRID_ROWS);
              if (!pos) break;
              newItems.push({ id, x: pos.x, y: pos.y, w: 1, h: 1 });
              for (let dy = 0; dy < 1; dy++)
                for (let dx = 0; dx < 1; dx++)
                  occupied.add(`${pos.x + dx},${pos.y + dy}`);
            }
            items = [...items, ...newItems];
            usuariosAPI.savePreferencias(CLAVE_LAYOUT, { items }).catch(() => {});
          }
          setLayoutItems(items);
        } else if (data && Array.isArray(data.gridLayout) && data.gridLayout.length >= 25) {
          items = gridLayoutToItems(data.gridLayout, 5);
          setLayoutItems(items);
        } else {
          items = defaultItems(defaultOrder, GRID_COLS);
          setLayoutItems(items);
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar layout dashboard:', e);
        setLayoutItems(defaultItems(defaultOrder, GRID_COLS));
      } finally {
        if (!cancelled) setLoadingPref(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, defaultOrder]);

  // Quitar del layout ítems que ya no están en el dashboard (ficha desactivada o sacada)
  useEffect(() => {
    if (layoutItems.length === 0) return;
    const validSet = new Set(defaultOrder);
    const validItems = layoutItems.filter((it) => validSet.has(it.id));
    if (validItems.length >= layoutItems.length) return;
    setLayoutItems(validItems);
    usuariosAPI.savePreferencias(CLAVE_LAYOUT, { items: validItems }).catch(() => {});
  }, [defaultOrder, layoutItems]);

  // Cargar valores de fichas custom que están en el layout
  useEffect(() => {
    const fichaIds = layoutItems
      .map((it) => (String(it.id).match(/^ficha-(\d+)$/) ? it.id : null))
      .filter(Boolean);
    if (fichaIds.length === 0) return;
    let cancelled = false;
    const load = async () => {
      const next = {};
      const params = {};
      for (const cardId of fichaIds) {
        if (cancelled) return;
        const id = cardId.replace(/^ficha-/, '');
        try {
          const res = await informesAPI.dashboardFicha(id, params);
          if (cancelled) return;
          if (res.success && res.data) next[cardId] = res.data;
        } catch (e) {
          if (!cancelled) next[cardId] = { titulo: 'Ficha', valor: '-', subtitle: 'Error al cargar', formato: 'numero' };
        }
      }
      if (!cancelled) setFichaValues((prev) => ({ ...prev, ...next }));
    };
    load();
    return () => { cancelled = true; };
  }, [layoutItems]);

  const saveLayout = useCallback(
    (items) => {
      usuariosAPI.savePreferencias(CLAVE_LAYOUT, { items }).catch(() => {
        toast.error('No se pudo guardar el diseño del dashboard');
      });
    },
    []
  );

  const validOrderSet = useMemo(() => new Set(defaultOrder), [defaultOrder]);
  const itemsToRender = useMemo(
    () => layoutItems.filter((it) => validOrderSet.has(it.id)),
    [layoutItems, validOrderSet]
  );
  const occupied = useMemo(() => occupiedSet(itemsToRender), [itemsToRender]);

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    const el = e.currentTarget;
    if (el) el.classList.add('dashboard-card-dragging');
  };

  const handleDragEnd = (e) => {
    const el = e.currentTarget;
    if (el) el.classList.remove('dashboard-card-dragging');
    setDragId(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const t = e.currentTarget;
    if (t && t.classList && !t.classList.contains('dashboard-cell-drag-over')) {
      t.classList.add('dashboard-cell-drag-over');
    }
  };

  const handleDragLeave = (e) => {
    const t = e.currentTarget;
    if (t && t.classList) t.classList.remove('dashboard-cell-drag-over');
  };

  const handleDrop = (e, dropX, dropY) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dashboard-cell-drag-over');
    const sourceId = dragId || e.dataTransfer.getData('text/plain');
    if (!sourceId) return;
    const sourceItem = layoutItems.find((it) => it.id === sourceId);
    if (!sourceItem) return;
    const targetItem = getItemAt(layoutItems, dropX, dropY);
    const next = layoutItems.map((it) => ({ ...it }));

    if (targetItem && targetItem.id !== sourceId) {
      const src = next.find((it) => it.id === sourceId);
      const tgt = next.find((it) => it.id === targetItem.id);
      if (src && tgt) {
        const sx = src.x;
        const sy = src.y;
        src.x = tgt.x;
        src.y = tgt.y;
        tgt.x = sx;
        tgt.y = sy;
      }
    } else {
      const src = next.find((it) => it.id === sourceId);
      if (src && (src.x !== dropX || src.y !== dropY)) {
        src.x = dropX;
        src.y = dropY;
      }
    }
    setLayoutItems(next);
    setDragId(null);
    saveLayout(next);
  };

  const handleResizeStart = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      active: true,
      itemId: item.id,
      startW: item.w,
      startH: item.h,
      startX: e.clientX,
      startY: e.clientY,
    };
    const onMove = (ev) => {
      if (!resizeRef.current.active || resizeRef.current.itemId !== item.id) return;
      const grid = gridRef.current;
      if (!grid) return;
      const rect = grid.getBoundingClientRect();
      const cellW = rect.width / GRID_COLS;
      const cellH = rect.height / GRID_ROWS;
      const deltaCols = Math.round((ev.clientX - resizeRef.current.startX) / cellW);
      const deltaRows = Math.round((ev.clientY - resizeRef.current.startY) / cellH);
      const startW = resizeRef.current.startW;
      const startH = resizeRef.current.startH;
      setLayoutItems((prev) => {
        const next = prev.map((it) => {
          if (it.id !== item.id) return it;
          const w = Math.max(MIN_W, Math.min(GRID_COLS - it.x, startW + deltaCols));
          const h = Math.max(MIN_H, Math.min(GRID_ROWS - it.y, startH + deltaRows));
          return { ...it, w, h };
        });
        layoutItemsRef.current = next;
        return next;
      });
    };
    const onUp = () => {
      if (resizeRef.current.active && resizeRef.current.itemId && layoutItemsRef.current.length) {
        saveLayout(layoutItemsRef.current);
      }
      resizeRef.current.active = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  const renderCard = (cardId, item) => {
    if (!cardId) return null;
    const fichaMatch = cardId.match(/^ficha-(\d+)$/);
    if (fichaMatch) {
      const data = fichaValues[cardId];
      const titulo = data?.titulo || 'Ficha';
      const subtitle = data?.subtitle || '';
      const area = item ? item.w * item.h : 1;

      if (data?.formato === 'grafico_torta') {
        return (
          <>
            <span className="dashboard-card-grip" title="Arrastrar para reordenar">⋮⋮</span>
            <h3>{titulo}</h3>
            {renderFichaTortaAbonos(data, area)}
            {subtitle && <p className="stat-subtitle">{subtitle}</p>}
          </>
        );
      }

      const valor = data?.formato === 'moneda' ? fmt(data?.valor) : (data?.valor ?? '-');
      return (
        <>
          <span className="dashboard-card-grip" title="Arrastrar para reordenar">⋮⋮</span>
          <h3>{titulo}</h3>
          <p className="stat-value">{valor}</p>
          {subtitle && <p className="stat-subtitle">{subtitle}</p>}
        </>
      );
    }
    return null;
  };

  const cardClass = (cardId) => {
    if (cardId && cardId.startsWith('ficha-')) return 'stat-card dashboard-card';
    return 'stat-card dashboard-card';
  };

  const cardClassExtra = (cardId, fichaData) => {
    if (fichaData?.formato === 'grafico_torta') return ' dashboard-card--grafico-torta';
    return '';
  };

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>

      {customFichas.length === 0 ? (
        <p className="dashboard-empty-message" style={{ marginTop: 24, color: '#666', fontSize: 15 }}>
          No hay fichas en el dashboard. Cree fichas desde <strong>Fichas del dashboard</strong> en el menú y actívelas para mostrarlas aquí.
        </p>
      ) : (
        <div
          ref={gridRef}
          className="dashboard-grid dashboard-grid-resizable"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
            gridTemplateRows: `repeat(${GRID_ROWS}, minmax(100px, auto))`,
            gap: 16,
          }}
        >
          {itemsToRender.map((item) => {
            const area = item.w * item.h;
            const fichaData = fichaValues[item.id];
            const sizeClass =
              area >= 8 ? 'dashboard-card-size-large'
              : area >= 4 ? 'dashboard-card-size-medium'
              : 'dashboard-card-size-small';
            return (
              <div
                key={item.id}
                className={`${cardClass(item.id)}${cardClassExtra(item.id, fichaData)} ${sizeClass}`}
                draggable
                onDragStart={(e) => handleDragStart(e, item.id)}
                onDragEnd={handleDragEnd}
                style={{
                  gridColumn: `${item.x + 1} / span ${item.w}`,
                  gridRow: `${item.y + 1} / span ${item.h}`,
                }}
              >
                {renderCard(item.id, item)}
                <span
                  className="dashboard-card-resize"
                  title="Redimensionar"
                  onMouseDown={(e) => handleResizeStart(e, item)}
                />
              </div>
            );
          })}
          {Array.from({ length: GRID_ROWS }, (_, row) =>
            Array.from({ length: GRID_COLS }, (_, col) => {
              const key = `${col},${row}`;
              if (occupied.has(key)) return null;
              return (
                <div
                  key={key}
                  className="dashboard-cell dashboard-cell-empty"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, col, row)}
                  style={{
                    gridColumn: col + 1,
                    gridRow: row + 1,
                  }}
                >
                  <span className="dashboard-cell-placeholder">Soltar aquí</span>
                </div>
              );
            })
          ).flat()}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
